// © Microsoft Corporation. All rights reserved.

import { DesignSystem, accentFillRest, accentFillSelected, neutralFillStealthActive, neutralFillToggleHover, neutralForegroundHint, neutralForegroundRest, neutralLayerCardContainer } from "@microsoft/fast-components-styles-msft";

import { applyLocalizedProperty } from "@microsoft/fast-jss-utilities";

const messageMaxClampedLines = 3;

export const ProfileCommentsStyles = (config: DesignSystem) => ({
    profileCommentsContainer: {
        margin: "16px 20px 24px",
        padding: "0",
        "list-style": "none",

        "& .cwt-profile-comment-item-list": {
            padding: "0",
            margin: "0"
        },

        "& .cwt-profile-comment": {
            padding: "20px 0",
            "border-bottom": "1px solid rgba(143, 158, 205, 0.2)",

            "&:last-of-type": {
                border: "none"
            },

            "& .cwt-open-content-link:hover": {
                "text-decoration": "none"
            },

            "& .cwt-header": {
                "margin-bottom": "4px",

                "& .cwt-title": {
                    "font-size": "16px",
                    "font-weight": "600",
                    color: neutralForegroundRest
                },
                "& .cwt-posted-at": {
                    color: neutralFillToggleHover,
                    "font-size": "14px"
                },
                "& .dot-before:before": {
                    opacity: ".55"
                },
                "& .cwt-comment-config-container": {
                    float: applyLocalizedProperty("right", "left", config.direction),
                    "margin-top": "3px",

                    "& .cwt-icon": {
                        color: neutralForegroundHint,

                        "&:hover": {
                            color: neutralForegroundRest // #2b2b2b used instead of #000000
                        }
                    }
                }
            },

            "& .cwt-message-engagement-container": {
                display: "flex",
                "flex-direction": "column",
                margin: "0 -5px 8px",
                padding: "8px 5px 5px",
                "border-radius": "8px",
                cursor: "pointer",

                "&:hover": {
                    background: neutralFillStealthActive
                }
            },

            "& .cwt-message": {
                margin: "0",
                "font-size": "14px",
                "line-height": "18px",
                color: neutralForegroundRest, // #2b2b2b used instead of #3c4046
                "word-break": "break-word",

                "& *": {
                    display: "inline"
                },

                "& .cwt-name.cwt-link": {
                    color: accentFillRest,
                    "font-weight": "normal",

                    "&::before": {
                        content: "'@'",
                        display: "inline"
                    }
                },

                "& .cwt-comment-deleted-message": {
                    display: "inline",
                    "font-style": "italic",
                    color: neutralForegroundHint,

                    "& .cwt-icon": {
                        margin: "-4px 4px 0",
                        color: neutralForegroundHint,
                        fill: neutralForegroundHint,
                        display: "inline-flex",
                        "vertical-align": "middle"
                    }
                },

                "& > .cwt-clamp": {
                    display: "-webkit-box",
                    "-webkit-line-clamp": messageMaxClampedLines,
                    "-webkit-box-orient": "vertical",
                    overflow: "hidden"
                },

                "& .cwt-show-clamped-btn": {
                    color: accentFillRest,
                    "font-size": "12px"
                }
            },

            "& .cwt-engagements": {
                display: "inline-flex",
                "align-items": "center",
                margin: "10px -4px 0",
                padding: "0",
                height: "16px",
                "max-height": "16px",
                "min-height": "16px",

                "& .cwt-replies-link": {
                    margin: "0 6px",
                    "font-size": "14px",
                    color: accentFillSelected, // Changed from #0078D4 to #005393 because it was unreadable

                    "& .cwt-icon-label": {
                        "font-size": "12px"
                    }
                },

                "& .cwt-reaction-counts": {
                    display: "flex",
                    "align-items": "center",

                    "& > .cwt-icon": {
                        margin: "0",
                        padding: "0 4px",
                        "font-size": "18px !important",

                        "& .cwt-icon-label": {
                            "font-size": "14px"
                        },

                        "& .cwt-icon-vector": {
                            [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "4px",

                            "&:last-child": {
                                [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "0"
                            }
                        }
                    }
                }
            },

            "& .cwt-content-info": {
                display: "flex",
                "align-items": "center",
                padding: "12px",
                border: "1px solid #DDDDDD",
                "border-radius": "8px",

                "&:hover": {
                    background: neutralLayerCardContainer
                },

                "& .cwt-content-image": {
                    margin: "0",
                    "object-fit": "cover",
                    width: "68px",
                    height: "45px",
                    border: "none",
                    "border-radius": "4px"
                },

                "& .cwt-content-title": {
                    [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: "14px",
                    "font-size": "14px",
                    "font-weight": "600",
                    color: neutralFillToggleHover
                }
            }
        },

        "& .cwt-show-more-comments-btn": {
            margin: "16px 0 8px",
            "font-weight": "600",
            color: "#0076D6",
            fill: "currentColor",
            opacity: "1"
        }
    },
    profileCommentsDeleteMenuPopover: {
        bottom: "16px",
        [applyLocalizedProperty("right", "left", config.direction)]: "-16px"
    },

    profileCommentsMiniBreakdownPopover: {
        [applyLocalizedProperty("left", "right", config.direction)]: "-12px"
    }
});